import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { useOrgHall } from "@/hooks/useOrgHall";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import { Plus, Play, Trophy, Users, Loader2, CheckCircle, ArrowLeft, Save } from "lucide-react";
import { generateAmericanoRound, generateMexicanoRound } from "@/lib/tournamentPairing";
import LevelBadge from "@/components/level/LevelBadge";
import TournamentLeaderboard from "@/components/tournaments/TournamentLeaderboard";
import type { Tables } from "@/integrations/supabase/types";

type CourtWithHall = Tables<"courts"> & {
  halls?: { name: string; opening_time: string; closing_time: string } | null;
};

type _TournamentWithPlayers = Tables<"tournaments"> & {
  tournament_players: { id: string; user_id: string; total_points: number | null }[];
};

type TournamentPlayer = Tables<"tournament_players"> & {
  profile?: { id: string; full_name: string | null; level: number | null } | null;
};

type TournamentRound = Tables<"tournament_rounds"> & {
  tournament_match_results: Tables<"tournament_match_results">[];
};

export default function AdminTournaments() {
  const { t } = useTranslation("admin");
  const { user } = useAuth();
  const { organization } = useOrganization();
  const { courts } = useOrgHall();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState("");
  const [tFormat, setTFormat] = useState("americano");
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [startTime, setStartTime] = useState("10:00");
  const [endTime, setEndTime] = useState("14:00");
  const [maxPlayers, setMaxPlayers] = useState("16");
  const [pointsPerMatch, setPointsPerMatch] = useState("32");
  const [pricePerPlayer, setPricePerPlayer] = useState("0");
  const [description, setDescription] = useState("");
  const [selectedCourts, setSelectedCourts] = useState<string[]>([]);
  const [playerResultEntry, setPlayerResultEntry] = useState(true);
  const [resultConfirmation, setResultConfirmation] = useState("opponent_confirm");
  const [autoAcceptMinutes, setAutoAcceptMinutes] = useState(10);
  const [autoAdvanceRounds, setAutoAdvanceRounds] = useState(false);

  const activeCourts = (courts as CourtWithHall[] ?? []).filter((c) => c.status === "active");

  const { data: tournaments = [], isLoading } = useQuery({
    queryKey: ["admin-tournaments", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tournaments")
        .select("*, tournament_players(id, user_id, total_points)")
        .eq("organization_id", organization!.id)
        .order("date", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!organization,
  });

  const selectedTournament = tournaments.find((t) => t.id === selectedId);

  // Fetch players with profiles for detail view
  const { data: detailPlayers = [] } = useQuery({
    queryKey: ["admin-tournament-players", selectedId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tournament_players")
        .select("*")
        .eq("tournament_id", selectedId!)
        .order("total_points", { ascending: false });
      if (error) throw error;
      const userIds = (data ?? []).map((p) => p.user_id);
      if (userIds.length === 0) return [];
      const { data: profiles } = await supabase.from("public_profiles" as any).select("id, full_name, level").in("id", userIds);
      const profileMap = new Map((profiles ?? []).map((p) => [p.id, p]));
      return (data ?? []).map((p) => ({ ...p, profile: profileMap.get(p.user_id) })) as TournamentPlayer[];
    },
    enabled: !!selectedId,
  });

  // Fetch rounds for detail view
  const { data: detailRounds = [] } = useQuery({
    queryKey: ["admin-tournament-rounds", selectedId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tournament_rounds")
        .select("*, tournament_match_results(*)")
        .eq("tournament_id", selectedId!)
        .order("round_number", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!selectedId,
  });

  // Fetch profile names for match display
  const allPlayerIds = [...new Set((detailRounds as TournamentRound[]).flatMap((r) =>
    (r.tournament_match_results ?? []).flatMap((m) => [...(m.team1_players ?? []), ...(m.team2_players ?? [])])
  ))];
  const { data: matchProfiles = [] } = useQuery({
    queryKey: ["match-profiles", allPlayerIds.join(",")],
    queryFn: async () => {
      if (allPlayerIds.length === 0) return [];
      const { data } = await supabase.from("public_profiles" as any).select("id, full_name").in("id", allPlayerIds);
      return data ?? [];
    },
    enabled: allPlayerIds.length > 0,
  });
  const nameMap = new Map(matchProfiles.map((p) => [p.id, p.full_name ?? "?"]));
  const getNames = (ids: string[]) => ids.map((id) => nameMap.get(id) ?? "?").join(" & ");

  const createMutation = useMutation({
    mutationFn: async () => {
      // Sprint D PR-AC dual-write: form value is kr, dual-write to _minor (öre).
      // Defensive trigger trg_dual_write_tournaments_minor backfills if missing.
      // court_cost_total + admin_fee_per_player default to 0 in DB (trigger fills _minor).
      const pricePerPlayerKr = parseInt(pricePerPlayer);
      const { error } = await supabase.from("tournaments").insert({
        organization_id: organization!.id,
        created_by: user!.id,
        name,
        format: tFormat,
        date,
        start_time: startTime,
        end_time: endTime,
        max_players: parseInt(maxPlayers),
        points_per_match: parseInt(pointsPerMatch),
        price_per_player: pricePerPlayerKr,
        price_per_player_minor: pricePerPlayerKr * 100,
        currency: "SEK",
        courts_used: selectedCourts.length > 0 ? selectedCourts : null,
        description: description.trim() || null,
        status: "draft",
        player_result_entry: playerResultEntry,
        result_confirmation: resultConfirmation,
        auto_accept_minutes: autoAcceptMinutes,
        auto_advance_rounds: autoAdvanceRounds,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-tournaments"] });
      toast.success(t("admin:toast.tournamentCreated"));
      setShowCreate(false);
      setName(""); setDescription(""); setPricePerPlayer("0"); setSelectedCourts([]);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from("tournaments").update({ status }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-tournaments"] });
      toast.success(t("admin:toast.statusUpdated"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const generateRoundMutation = useMutation({
    mutationFn: async (tournamentId: string) => {
      const tournament = tournaments.find((t) => t.id === tournamentId);
      if (!tournament) throw new Error(t("tournamentsPage.tournamentNotFound"));

      const players = tournament.tournament_players ?? [];
      if (players.length < 4) throw new Error(t("tournamentsPage.minPlayersRequired"));

      const { data: existingRounds } = await supabase
        .from("tournament_rounds")
        .select("round_number, tournament_match_results(team1_players, team2_players)")
        .eq("tournament_id", tournamentId)
        .order("round_number", { ascending: true });

      const nextRound = ((existingRounds ?? []).length > 0
        ? Math.max(...(existingRounds ?? []).map((r) => r.round_number))
        : 0) + 1;

      // Rebuild partner history from all previous rounds
      const partnerHistory = new Map<string, Set<string>>();
      for (const round of existingRounds ?? []) {
        for (const match of (round as TournamentRound).tournament_match_results ?? []) {
          for (const team of [match.team1_players, match.team2_players]) {
            if (team?.length === 2) {
              const [a, b] = team;
              if (!partnerHistory.has(a)) partnerHistory.set(a, new Set());
              if (!partnerHistory.has(b)) partnerHistory.set(b, new Set());
              partnerHistory.get(a)!.add(b);
              partnerHistory.get(b)!.add(a);
            }
          }
        }
      }

      const courtCount = Math.max(1, Math.floor(players.length / 4));
      const playerScores = players.map((p: { user_id: string; total_points: number | null }) => ({
        userId: p.user_id,
        totalPoints: p.total_points ?? 0,
      }));

      let matches;
      if (tournament.format === "mexicano") {
        matches = generateMexicanoRound(playerScores, courtCount, nextRound, partnerHistory);
      } else {
        matches = generateAmericanoRound(
          playerScores.map((p) => p.userId),
          courtCount,
          partnerHistory
        );
      }

      const { data: round, error: roundErr } = await supabase
        .from("tournament_rounds")
        .insert({
          tournament_id: tournamentId,
          round_number: nextRound,
          status: "active",
          matches: matches as unknown as Tables<"tournament_rounds">["matches"],
        })
        .select()
        .single();
      if (roundErr) throw roundErr;

      const tCourts = tournament.courts_used ?? [];
      const matchResults = matches.map((m, _i) => ({
        round_id: round.id,
        court_id: tCourts[m.courtIndex] ?? activeCourts[m.courtIndex]?.id ?? null,
        team1_players: m.team1,
        team2_players: m.team2,
        status: "pending",
      }));

      const { error: matchErr } = await supabase.from("tournament_match_results").insert(matchResults);
      if (matchErr) throw matchErr;

      // If first round and status is still registration, make it active
      if (nextRound === 1) {
        await supabase.from("tournaments").update({ status: "active" }).eq("id", tournamentId);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-tournaments"] });
      qc.invalidateQueries({ queryKey: ["admin-tournament-rounds", selectedId] });
      toast.success(t("admin:toast.roundGenerated"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Score state for inline editing
  const [editScores, setEditScores] = useState<Record<string, { s1: string; s2: string }>>({});

  const saveResultMutation = useMutation({
    mutationFn: async ({ matchId, score1, score2, tournamentId }: { matchId: string; score1: number; score2: number; tournamentId: string }) => {
      // Update match result
      const { error } = await supabase
        .from("tournament_match_results")
        .update({ score_team1: score1, score_team2: score2, status: "completed" })
        .eq("id", matchId);
      if (error) throw error;

      // Get the match to know which players to update
      const { data: match } = await supabase
        .from("tournament_match_results")
        .select("team1_players, team2_players")
        .eq("id", matchId)
        .single();
      if (!match) return;

      // Update player points
      for (const pid of match.team1_players ?? []) {
        const current = detailPlayers.find((p) => p.user_id === pid)?.total_points ?? 0;
        await supabase
          .from("tournament_players")
          .update({ total_points: current + score1 })
          .eq("tournament_id", tournamentId)
          .eq("user_id", pid);
      }
      for (const pid of match.team2_players ?? []) {
        const current = detailPlayers.find((p) => p.user_id === pid)?.total_points ?? 0;
        await supabase
          .from("tournament_players")
          .update({ total_points: current + score2 })
          .eq("tournament_id", tournamentId)
          .eq("user_id", pid);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-tournament-rounds", selectedId] });
      qc.invalidateQueries({ queryKey: ["admin-tournament-players", selectedId] });
      qc.invalidateQueries({ queryKey: ["admin-tournaments"] });
      qc.invalidateQueries({ queryKey: ["tournament-leaderboard", selectedId] });
      toast.success(t("admin:toast.resultSaved"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleCourtSelection = (courtId: string) => {
    setSelectedCourts((prev) =>
      prev.includes(courtId) ? prev.filter((id) => id !== courtId) : [...prev, courtId]
    );
  };

  if (isLoading) {
    return <div className="space-y-3">{[1, 2].map(i => <Skeleton key={i} className="h-24" />)}</div>;
  }

  // ============ Detail view ============
  if (selectedId && selectedTournament) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => setSelectedId(null)} className="gap-1">
          <ArrowLeft className="h-4 w-4" /> {t("common.back")}
        </Button>

        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">{selectedTournament.name}</h2>
            <div className="flex gap-2 mt-1">
              <Badge variant="outline">{t(`tournamentsPage.formatLabels.${selectedTournament.format}`)}</Badge>
              <Badge variant="secondary">{t(`tournamentsPage.statusLabels.${selectedTournament.status}`)}</Badge>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {selectedTournament.status === "draft" && (
              <Button size="sm" onClick={() => updateStatusMutation.mutate({ id: selectedId, status: "registration" })}>
                {t("tournamentsPage.openRegistration")}
              </Button>
            )}
            {selectedTournament.status === "registration" && (
              <Button size="sm" className="gap-1"
                onClick={() => generateRoundMutation.mutate(selectedId)}
                disabled={generateRoundMutation.isPending || detailPlayers.length < 4}>
                {generateRoundMutation.isPending ? <Loader2 className="animate-spin h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                {t("tournamentsPage.startTournament")}
              </Button>
            )}
            {selectedTournament.status === "active" && (
              <>
                <Button size="sm" variant="outline" className="gap-1"
                  onClick={() => generateRoundMutation.mutate(selectedId)}
                  disabled={generateRoundMutation.isPending}>
                  {generateRoundMutation.isPending ? <Loader2 className="animate-spin h-3.5 w-3.5" /> : <Trophy className="h-3.5 w-3.5" />}
                  {t("tournamentsPage.newRound")}
                </Button>
                <Button size="sm" variant="outline" className="gap-1"
                  onClick={() => updateStatusMutation.mutate({ id: selectedId, status: "completed" })}>
                  <CheckCircle className="h-3.5 w-3.5" /> {t("tournamentsPage.finish")}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Players */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t("tournamentsPage.registeredPlayers")} ({detailPlayers.length}/{selectedTournament.max_players})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {detailPlayers.map((p, i) => (
              <div key={p.id} className="flex items-center gap-2 py-1">
                <span className="text-xs text-muted-foreground w-5 text-right">{i + 1}.</span>
                <span className="text-sm font-medium flex-1">{p.profile?.full_name ?? t("common.unknown")}</span>
                <LevelBadge level={p.profile?.level ?? 1} size="sm" />
                {selectedTournament.status === "active" && (
                  <span className="text-xs font-bold tabular-nums">{p.total_points}p</span>
                )}
              </div>
            ))}
            {detailPlayers.length === 0 && <p className="text-sm text-muted-foreground">{t("tournamentsPage.noPlayersYet")}</p>}
          </CardContent>
        </Card>

        {/* Live leaderboard for active/completed */}
        {(selectedTournament.status === "active" || selectedTournament.status === "completed") && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t("tournamentsPage.standings")}</CardTitle>
            </CardHeader>
            <CardContent>
              <TournamentLeaderboard tournamentId={selectedId} />
            </CardContent>
          </Card>
        )}

        {/* Rounds with result input */}
        {(detailRounds as TournamentRound[]).map((round) => (
          <Card key={round.id}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between">
                <span>{t("tournamentsPage.round")} {round.round_number}</span>
                <Badge variant="outline" className="text-xs">
                  {t(`tournamentsPage.roundStatus.${round.status === "completed" ? "completed" : round.status === "active" ? "active" : "pending"}`)}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(round.tournament_match_results ?? []).map((match, mi) => {
                const scores = editScores[match.id] ?? { s1: String(match.score_team1 ?? ""), s2: String(match.score_team2 ?? "") };
                const isCompleted = match.status === "completed";

                return (
                  <div key={match.id} className="rounded-lg border p-3 space-y-2">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{t("tournamentsPage.match")} {mi + 1}</span>
                      <Badge variant={isCompleted ? "default" : "outline"} className="text-[10px]">
                        {t(`tournamentsPage.roundStatus.${isCompleted ? "completed" : "pending"}`)}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                      <div className="text-sm font-medium text-right">
                        {getNames(match.team1_players ?? [])}
                      </div>
                      <div className="flex items-center gap-1">
                        <Input type="number"
                          className="w-12 h-8 text-center text-sm p-0"
                          value={scores.s1}
                          onChange={(e) => setEditScores((prev) => ({ ...prev, [match.id]: { ...scores, s1: e.target.value } }))}
                          disabled={isCompleted}
                        />
                        <span className="text-muted-foreground text-xs">–</span>
                        <Input type="number"
                          className="w-12 h-8 text-center text-sm p-0"
                          value={scores.s2}
                          onChange={(e) => setEditScores((prev) => ({ ...prev, [match.id]: { ...scores, s2: e.target.value } }))}
                          disabled={isCompleted}
                        />
                      </div>
                      <div className="text-sm font-medium">
                        {getNames(match.team2_players ?? [])}
                      </div>
                    </div>
                    {!isCompleted && scores.s1 !== "" && scores.s2 !== "" && (
                      <Button
                        size="sm"
                        className="w-full gap-1"
                        onClick={() => saveResultMutation.mutate({
                          matchId: match.id,
                          score1: parseInt(scores.s1),
                          score2: parseInt(scores.s2),
                          tournamentId: selectedId,
                        })}
                        disabled={saveResultMutation.isPending}
                      >
                        <Save className="h-3.5 w-3.5" /> {t("tournamentsPage.saveResult")}
                      </Button>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // ============ List view ============
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">{t("tournamentsPage.title")}</h2>
        <Button size="sm" onClick={() => setShowCreate(true)} className="gap-1">
          <Plus className="h-4 w-4" /> {t("tournamentsPage.newTournament")}
        </Button>
      </div>

      {tournaments.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">{t("tournamentsPage.noTournaments")}</p>
      ) : (
        tournaments.map((tourn) => (
          <Card key={tourn.id} className="cursor-pointer hover:border-primary/40 transition-colors" onClick={() => setSelectedId(tourn.id)}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-sm">{tourn.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">{t(`tournamentsPage.formatLabels.${tourn.format}`)}</Badge>
                    <Badge variant="secondary" className="text-xs">{t(`tournamentsPage.statusLabels.${tourn.status}`)}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {format(parseISO(tourn.date), "d MMM", { locale: sv })} · {tourn.start_time?.slice(0, 5)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    <Users className="h-3 w-3 inline mr-1" />{tourn.tournament_players?.length ?? 0}/{tourn.max_players} {t("tournamentsPage.players")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("tournamentsPage.createTournament")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground">{t("tournamentsPage.nameLabel")}</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder={t("tournamentsPage.namePlaceholder")} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t("tournamentsPage.formatLabel")}</Label>
              <Select value={tFormat} onValueChange={setTFormat}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="americano">{t("tournamentsPage.formatLabels.americano")}</SelectItem>
                  <SelectItem value="mexicano">{t("tournamentsPage.formatLabels.mexicano")}</SelectItem>
                  <SelectItem value="team_americano">{t("tournamentsPage.formatLabels.team_americano")}</SelectItem>
                  <SelectItem value="team_mexicano">{t("tournamentsPage.formatLabels.team_mexicano")}</SelectItem>
                  <SelectItem value="round_robin">{t("tournamentsPage.formatLabels.round_robin")}</SelectItem>
                  <SelectItem value="knockout">{t("tournamentsPage.formatLabels.knockout")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground">{t("tournamentsPage.dateLabel")}</Label>
                <Input type="date" value={date} onChange={e => setDate(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("tournamentsPage.startLabel")}</Label>
                <Input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("tournamentsPage.endLabel")}</Label>
                <Input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
              </div>
            </div>
            {/* Court selection */}
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">{t("tournamentsPage.courtsLabel")}</Label>
              <div className="space-y-1.5">
                {activeCourts.map((c) => (
                  <div key={c.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`court-${c.id}`}
                      checked={selectedCourts.includes(c.id)}
                      onCheckedChange={() => toggleCourtSelection(c.id)}
                    />
                    <Label htmlFor={`court-${c.id}`} className="text-sm cursor-pointer">{c.name}</Label>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground">{t("tournamentsPage.maxPlayers")}</Label>
                <NumberInput value={maxPlayers} onValueChange={v => setMaxPlayers(String(v))} />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("tournamentsPage.pointsPerMatch")}</Label>
                <NumberInput value={pointsPerMatch} onValueChange={v => setPointsPerMatch(String(v))} />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("tournamentsPage.priceLabel")}</Label>
                <NumberInput value={pricePerPlayer} onValueChange={v => setPricePerPlayer(String(v))} placeholder={t("tournamentsPage.pricePlaceholder")} />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t("tournamentsPage.descriptionLabel")}</Label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)} className="h-20" />
            </div>

            {/* Result Reporting Settings */}
            {["americano", "mexicano", "team_americano", "team_mexicano"].includes(tFormat) && (
              <div className="space-y-3 rounded-lg border p-3">
                <Label className="text-sm font-semibold">{t("tournamentsPage.resultReporting")}</Label>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm">{t("tournamentsPage.playerReportsResults")}</p>
                    <p className="text-xs text-muted-foreground">{t("tournamentsPage.insteadOfAdmin")}</p>
                  </div>
                  <Switch checked={playerResultEntry} onCheckedChange={setPlayerResultEntry} />
                </div>
                {playerResultEntry && (
                  <>
                    <div>
                      <Label className="text-xs">{t("tournamentsPage.confirmationMode")}</Label>
                      <Select value={resultConfirmation} onValueChange={setResultConfirmation}>
                        <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="opponent_confirm">{t("tournamentsPage.opponentConfirm")}</SelectItem>
                          <SelectItem value="auto_accept">{t("tournamentsPage.autoAccept")}</SelectItem>
                          <SelectItem value="admin_only">{t("tournamentsPage.adminOnly")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {resultConfirmation === "opponent_confirm" && (
                      <div>
                        <Label className="text-xs">{t("tournamentsPage.autoAcceptAfter")}</Label>
                        <NumberInput min={1} max={60} value={autoAcceptMinutes}
                          onValueChange={v => setAutoAcceptMinutes(v)}
                          className="h-9 text-sm w-24" />
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {t("tournamentsPage.autoAcceptDescription")}
                        </p>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm">{t("tournamentsPage.autoGenerateNextRound")}</p>
                        <p className="text-xs text-muted-foreground">
                          {t("tournamentsPage.autoGenerateDescription")}
                        </p>
                      </div>
                      <Switch checked={autoAdvanceRounds} onCheckedChange={setAutoAdvanceRounds} />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("common.cancel")}</Button>
            <Button onClick={() => createMutation.mutate()} disabled={!name || createMutation.isPending}>
              {createMutation.isPending ? <Loader2 className="animate-spin h-4 w-4" /> : t("common.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
